import { ApiError, ConflictError, NetworkError } from "@/errors/api";
import { authenticationStateKey, backendHostName } from "@/utilities/constants";

/**
 * `fetch` only rejects when the request never made it to the server. Translate
 * that into our own error type so callers never have to deal with the raw,
 * browser-specific failure message.
 */
const sendRequest = async (request: Request): Promise<Response> => {
  try {
    return await fetch(request);
  } catch (error) {
    throw new NetworkError(error);
  }
};

export const fetchApi = async <T>(
  url: string,
  options: RequestInit,
): Promise<T> => {
  const request = new Request(`${backendHostName}${url}`, options);
  const response = await sendRequest(request);

  if (response.status === 401) {
    // properly sign out the user
    window.location.href = "/logout";
    // clear local storage auth entry
    localStorage.removeItem(authenticationStateKey);

    // never-resolving promise - this way the redirect will happen before
    // the UI knows about the error
    return new Promise<T>(() => {});
  }

  if (response.status === 409) {
    const errorBody = await response.json();
    throw new ConflictError(
      errorBody?.message,
      // Hardcoded to avoid importing from generated models - orval parses this
      // file as a mutator before generation, causing errors if it imports
      // the ErrorCode enum from the generated output folder.
      errorBody?.errorCode || "GENERIC_ERROR",
    );
  }

  if (response.status >= 400) {
    const errorBody = await response.json();

    throw new ApiError(
      response.status,
      errorBody?.message,
      errorBody?.errorCode,
    );
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return (await response.json()) as T;
};

export const fetchFile = async (
  url: string,
  options: RequestInit,
): Promise<Blob> => {
  const request = new Request(`${backendHostName}${url}`, options);
  const response = await sendRequest(request);

  if (response.status !== 200) {
    throw new Error("Could not fetch file");
  }

  return response.blob();
};
